import React from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip,
} from "@mui/material";
import PaymentIcon   from "@mui/icons-material/Payment";
import PeopleIcon    from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptIcon   from "@mui/icons-material/Receipt";
import moment from "moment";

const cardSx = {
  p: 3,
  backgroundColor: "#ffffff",
  border: "1px solid rgba(26,26,26,0.09)",
  borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const thSx = {
  color: "#6b6b6b", fontWeight: 600, fontSize: "11px",
  textTransform: "uppercase", letterSpacing: "0.4px",
  fontFamily: "'Rubik', sans-serif",
  borderBottom: "1px solid rgba(26,26,26,0.09)",
  backgroundColor: "#F2EDE4",
};

const StatCard = ({ label, value, subValue, icon, color = "#c01e2e" }) => (
  <Paper sx={{ ...cardSx, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
    <Box>
      <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 1, fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
      <Typography sx={{ color: "#1a1a1a", fontSize: "26px", fontWeight: 700, fontFamily: "'Rubik', sans-serif", lineHeight: 1 }}>{value}</Typography>
      {subValue && <Typography sx={{ color: "#aaa", fontSize: "12px", mt: 0.5, fontFamily: "'Rubik', sans-serif" }}>{subValue}</Typography>}
    </Box>
    <Box sx={{ width: 44, height: 44, borderRadius: "11px", backgroundColor: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
    </Box>
  </Paper>
);

export default function FlightSchoolBilling({ school }) {
  const activeStudents  = school?.active_students_count || 0;
  const includedSeats   = school?.included_seats || 0;
  const extraSeats      = Math.max(0, activeStudents - includedSeats);
  const monthlyStudentFee = school?.monthly_student_fee ?? 0;
  const extraSeatsCost  = extraSeats * monthlyStudentFee;
  const invoices        = school?.billing?.invoices || [];
  const aiUsageCost     = school?.billing?.ai_usage_cost || 0;

  return (
    <Box>
      {/* Summary Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: 4 }}>
        <StatCard label="Included Seats"        value={includedSeats}                           icon={<PeopleIcon />}    color="#16a34a" subValue="Base contract allocation" />
        <StatCard label="Extra Students"        value={extraSeats}                              icon={<PeopleIcon />}    color={extraSeats > 0 ? "#d97706" : "#aaa"} subValue={`£${monthlyStudentFee}/student/month`} />
        <StatCard label="Extra Students Cost"   value={`£${extraSeatsCost.toLocaleString()}`}   icon={<PaymentIcon />}   color="#c01e2e" subValue="Monthly charge" />
        <StatCard label="AI Usage Cost"         value={`£${aiUsageCost.toFixed(2)}`}             icon={<TrendingUpIcon />} color="#2563eb" subValue="This billing period" />
      </Box>

      {/* Seat Breakdown */}
      <Paper sx={{ ...cardSx, mb: 4 }}>
        <Typography sx={{ color: "#c01e2e", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", mb: 3, fontFamily: "'Rubik', sans-serif" }}>
          Seat Allocation
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
          <Box>
            <Typography sx={{ color: "#aaa", fontSize: "12px", mb: 1, fontFamily: "'Rubik', sans-serif" }}>Active Students</Typography>
            <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>
              {activeStudents} of {includedSeats} included
              {extraSeats > 0 && (
                <Chip label={`+${extraSeats} billable`} size="small"
                  sx={{ ml: 1, backgroundColor: "rgba(217,119,6,0.1)", color: "#d97706", fontSize: "11px", height: "20px", fontFamily: "'Rubik', sans-serif" }} />
              )}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ color: "#aaa", fontSize: "12px", mb: 1, fontFamily: "'Rubik', sans-serif" }}>Seat Utilisation</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flex: 1, height: 8, backgroundColor: "rgba(26,26,26,0.1)", borderRadius: 4, overflow: "hidden" }}>
                <Box sx={{
                  width: `${Math.min(100, (activeStudents / Math.max(includedSeats, 1)) * 100)}%`,
                  height: "100%",
                  backgroundColor: activeStudents > includedSeats ? "#d97706" : activeStudents / includedSeats > 0.8 ? "#d97706" : "#16a34a",
                  transition: "width 0.3s ease",
                }} />
              </Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "12px", minWidth: 40, fontFamily: "'Rubik', sans-serif" }}>
                {Math.round((activeStudents / Math.max(includedSeats, 1)) * 100)}%
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* AI Usage (if charging enabled) */}
      {school?.ai_settings?.charge_excess_usage && (
        <Paper sx={{ ...cardSx, mb: 4 }}>
          <Typography sx={{ color: "#c01e2e", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", mb: 3, fontFamily: "'Rubik', sans-serif" }}>
            AI Usage Charges
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
            <Box>
              <Typography sx={{ color: "#aaa", fontSize: "12px", mb: 1, fontFamily: "'Rubik', sans-serif" }}>Voice AI Rate</Typography>
              <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>£{school.ai_settings.voice_ai_price_per_minute || 0.40} / minute</Typography>
            </Box>
            <Box>
              <Typography sx={{ color: "#aaa", fontSize: "12px", mb: 1, fontFamily: "'Rubik', sans-serif" }}>Ground Instructor Rate</Typography>
              <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>£{school.ai_settings.ground_instructor_price_per_request || 0.10} / request</Typography>
            </Box>
            <Box>
              <Typography sx={{ color: "#aaa", fontSize: "12px", mb: 1, fontFamily: "'Rubik', sans-serif" }}>Total This Period</Typography>
              <Typography sx={{ color: "#c01e2e", fontSize: "14px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>£{aiUsageCost.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Billing History */}
      <Paper sx={cardSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <ReceiptIcon sx={{ color: "#c01e2e", fontSize: 18 }} />
          <Typography sx={{ color: "#c01e2e", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "'Rubik', sans-serif" }}>Billing History</Typography>
        </Box>
        {invoices.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {["Date", "Description", "Amount", "Status"].map((h) => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv, i) => (
                  <TableRow key={inv._id || i} sx={{ "&:hover": { backgroundColor: "#F7F4EF" }, "& td": { borderBottom: "1px solid rgba(26,26,26,0.06)" } }}>
                    <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{moment(inv.date).format("DD MMM YYYY")}</TableCell>
                    <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{inv.description}</TableCell>
                    <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>£{inv.amount?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip label={inv.status} size="small"
                        color={inv.status === "paid" ? "success" : inv.status === "pending" ? "warning" : "default"}
                        sx={{ borderRadius: "6px", fontSize: "11px", textTransform: "capitalize", fontFamily: "'Rubik', sans-serif" }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ color: "#aaa", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>No billing history available</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
